import { ComponentProps, Fragment, ReactElement, ReactNode } from 'react';

import { Enumerate, ParseInt } from '@pukka/common/types';
import { useSnapshot } from '@pukka/valtio';

import { Button } from '../Button';
import { Modal } from '../Dialog';
import { Loader } from '../Loader';

import { createSelectModalStateContext, SelectModalContext, useSelectModalState } from './SelectModal.state';
import { SelectModalButton } from './SelectModalButton';
import { SelectModalFilter } from './SelectModalFilter';
import { SelectModalItem } from './SelectModalItem';
import { SelectModalToggle } from './SelectModalToggle';
import { SelectModalToggleAll } from './SelectModalToggleAll';

interface SelectedProps<T> {
  readonly items: Set<T>;
  readonly selections: Set<T>;
  readonly disableds: Set<T>;
  readonly max: number;
  readonly min: number;
  readonly isAllSelected: boolean;
  readonly canSelectAll?: boolean;
  readonly errorMessage?: string | ReactNode;
}

interface ChildrenProps<T> {
  readonly items: Set<T>;
  readonly selections: Set<T>;
  readonly max: number;
  readonly filtered: Set<T>;
}

interface SelectModalProps<T> {
  readonly open?: boolean;
  readonly title: ReactNode;
  readonly isLoadingMoreItem?: boolean;
  readonly items?: T[] | Set<T>;
  readonly selectedItems?: T[] | Set<T>;
  readonly disabledItems?: T[] | Set<T>;
  readonly maxItems?: number;
  readonly minItems?: number;
  readonly filterBy?: string;
  readonly filterOptions?: SelectModalContext<T>['filter']['options'];
  readonly resetOnClose?: boolean;
  readonly trigger?: ReactNode;
  readonly buttons?: ComponentProps<typeof Button> | ((props: SelectedProps<T>) => ReactElement | null);
  readonly empty?: ReactNode;
  readonly canSelectAll?: boolean;
  readonly onEndReachedThreshold?: ParseInt<Exclude<`0.${Enumerate<100>}` | '1', '0.0' | `0.${Enumerate<10>}0`>>;
  readonly children?: ReactNode | ((props: ChildrenProps<T>) => ReactElement | null);
  readonly header?: ReactNode | ((props: SelectedProps<T>) => ReactElement | null);
  readonly footer?: ReactNode | ((props: SelectedProps<T>) => ReactElement | null);
  readonly errorMessage?: string | ReactNode;
  readonly onSubmit?: (values: Set<T>) => void;
  readonly onEndReached?: () => void;
  readonly onOpenChange?: (open: boolean) => void;
  readonly onClickCancel?: () => void;
}

function SelectModal<T>({
  open,
  title,
  isLoadingMoreItem,
  items,
  selectedItems,
  disabledItems,
  maxItems,
  minItems,
  filterBy,
  filterOptions,
  resetOnClose = true,
  trigger,
  children,
  buttons: Buttons,
  header: Header,
  footer: Footer,
  empty,
  canSelectAll,
  onEndReachedThreshold,
  errorMessage,
  onClickCancel,
  onSubmit,
  onEndReached,
  onOpenChange,
}: SelectModalProps<T>) {
  const state = useSelectModalState({
    items,
    selected: selectedItems,
    disabled: disabledItems,
    maxItems,
    minItems,
    filterBy,
    filterOptions,
    resetOnClose,
  });

  const { useUpdate, onCancel, selected, disabled, items: stateItems, max, min, filter, isAllSelected } = useSnapshot(state);
  const StateContext = createSelectModalStateContext<T>();
  const Wrapper = trigger ? Modal.Content : Fragment;

  useUpdate({
    items,
    selected: selectedItems,
    disabled: disabledItems,
    maxItems,
    minItems,
    filterBy,
    filterOptions,
  });

  const handleClickCancel = () => {
    onCancel();

    onClickCancel?.();
  };

  return (
    <Modal unwrap={!!trigger} open={open} onOpenChange={onOpenChange}>
      <StateContext.Provider value={state}>
        {trigger ? <Modal.Trigger asChild>{trigger}</Modal.Trigger> : null}
        <Wrapper>
          <Modal.Title>{title}</Modal.Title>
          {Header ? (
            <Modal.Header>
              {typeof Header === 'function' ? (
                <Header
                  selections={selected}
                  items={stateItems}
                  max={max}
                  min={min}
                  disableds={disabled}
                  isAllSelected={isAllSelected}
                  canSelectAll={canSelectAll}
                />
              ) : (
                Header
              )}
            </Modal.Header>
          ) : null}
          <Modal.Body
            className="h-96 px-0"
            onEndReachedThreshold={onEndReachedThreshold}
            onEndReached={isLoadingMoreItem ? undefined : onEndReached}
          >
            {empty && !filter.count ? empty : null}
            {typeof children === 'function'
              ? children({
                  filtered: filter.items,
                  items: stateItems,
                  max,
                  selections: selected,
                })
              : children}
            {isLoadingMoreItem ? (
              <div className="flex items-center justify-center p-2">
                <Loader />
              </div>
            ) : null}
          </Modal.Body>
          <Modal.Footer
            onCancel={handleClickCancel}
            buttons={
              Buttons && typeof Buttons === 'function' ? (
                <Buttons selections={selected} items={stateItems} max={max} min={min} disableds={disabled} isAllSelected={isAllSelected} />
              ) : (
                <SelectModalButton
                  selections={selected}
                  isAllSelected={isAllSelected}
                  max={max}
                  min={min}
                  {...(Buttons ?? {})}
                  onClick={onSubmit}
                />
              )
            }
          >
            {typeof Footer === 'function' ? (
              <Footer
                selections={selected}
                items={stateItems}
                max={max}
                min={min}
                disableds={disabled}
                isAllSelected={isAllSelected}
                errorMessage={errorMessage}
              />
            ) : (
              Footer
            )}
          </Modal.Footer>
        </Wrapper>
      </StateContext.Provider>
    </Modal>
  );
}

SelectModal.Button = SelectModalButton;
SelectModal.Filter = SelectModalFilter;
SelectModal.Close = Modal.Close;
SelectModal.Item = SelectModalItem;
SelectModal.Toggle = SelectModalToggle;
SelectModal.ToggleAll = SelectModalToggleAll;

export { SelectModal, type ChildrenProps };
